import { ref, useI18n } from "#imports";
import { applicationContext } from "@@/package/application/applicationContext";
import { useDialogStore } from "~~/application/store/dialog/dialogStore";
import { RecorderDataModel } from "~~/package/mapper/recorder/model/RecorderDataModel";
import { useLearningStore } from "~~/application/store/learning/learningStore";
import {useLearningAssessmentStore} from "~/store/learning/assessment/learningAssessmentStore";
import RecordeApi from "~~/package/apis/recorde/RecordeApi";
import { useUserStore } from "~~/application/store/user/userStore";
import { RecorderDomainType } from "~~/package/mapper/recorder/model/RecorderDomainType";
import { useGenericDialog } from "~/composables/useGenericDialog";
import { useLoadingStore } from "~~/application/store/loading/loadingStore";
import DialogType from "~/components/Common/Dialog/model/DialogType";
import { useFaroTracking } from "~/composables/useFaroTracking";

// 세그먼트가 헤더만 담긴 채 끝나면 ~100B 가 된다. 이보다 작으면 인코더가 프레임을 쓰지 못한 것으로 간주.
const MIN_VIDEO_BLOB_SIZE = 1024;
// 연속으로 이 횟수만큼 빈 녹화가 감지되면 녹화 불가로 판단해 중단 플로우에 진입한다.
// 표본은 세그먼트 회전(5초)마다 하나이므로 감지까지 최소 ~15초 걸린다(예전 1초 인터벌 시절엔 ~3초).
// caution 저장 중에는 회전이 멈추므로 그만큼(4~11초) 더 늦어진다 — 지속적 고장 감지용이지
// 즉각 감지용이 아니다.
const CONSECUTIVE_FAILURE_THRESHOLD = 3;
// 녹화 시작 직후에는 인코더/캡처가 안정화되기 전이라 작은 blob 이 정상적으로 나올 수 있다.
const HEALTH_CHECK_GRACE_MS = 5000;
// focus-out(탭 전환/개발자도구) 로 수명이 극단적으로 짧은 recorder 는 헤더-only 가 되므로 제외.
// 정상 세그먼트는 5초 이상이라 이 가드는 stop/save 경합 같은 예외 경로에만 걸린다.
const MIN_RECORDER_LIFETIME_MS = 700;
// abort 텔레메트리에 실어 보낼 직전 blob 크기 개수 (원인 판별용 — 언제부터 빈 blob 이 됐는지).
const HEALTH_CHECK_HISTORY_LIMIT = 10;
// VP8 은 Chrome 에서 항상 소프트웨어(libvpx) 인코더를 사용하므로, 불안정한 GPU 하드웨어
// 인코더(VP9 이 흔히 사용하는 VideoEncodeAccelerator) 경로를 원천적으로 우회한다.
// → 응시자가 "그래픽 가속 끄기" 를 하지 않아도 빈-파일(헤더-only) 증상을 막는 핵심 조치.
const RECORDER_MIME_TYPE_PREFERENCES = [
    'video/webm; codecs=vp8',
    'video/webm; codecs=vp9',
    'video/webm',
];

/** ASMT-PROCTOR-14: 평가 시작 전 화면 공유 점검 결과. */
export enum ScreenShareCheckResult {
    SUCCESS = 'SUCCESS',
    /** 전체 화면이 아닌 창/탭을 공유했다 — 재요청 대상. */
    NOT_FULL_SCREEN = 'NOT_FULL_SCREEN',
    /** 사용자가 공유 선택기를 닫았다 — 토스트만, 운영팀 알림 없음. */
    CANCELLED = 'CANCELLED',
    /** 권한 거부·장치 문제·선택기 없이 막힘(브라우저 정책) — OS별 해결 안내 + 운영팀 알림. */
    FAILED = 'FAILED',
}

/**
 * 사람이 공유 선택기를 보고 닫는 데 걸리는 최소 시간(ms). 이보다 빨리 온 거부는 선택기가 뜨지도 않은
 * 브라우저·정책 차단으로 본다 — 선택기는 뜨는 데만 수백 ms 가 걸리고, 정책 차단은 요청 즉시 돌아온다.
 */
export const MIN_PICKER_CANCEL_MS = 500;

export interface ScreenShareCheckOutcome {
    result: ScreenShareCheckResult;
    /** FAILED 일 때의 원인(DOMException.name). 운영팀 알림의 "실패 사유"로 그대로 전달된다. */
    reason?: string;
}

export const useLearningScreenRecordingService = () => {
    const config = { public: applicationContext.getConfig() };
    const router = useRouter();
    const stream = ref<MediaStream | null>(null);
    const dialogStore = useDialogStore();
    const loadingStore = useLoadingStore();
    const learningStore = useLearningStore();
    const assessmentStore = useLearningAssessmentStore();
    const userStore = useUserStore();
    const { t } = useI18n();
    const { openGenericAlert } = useGenericDialog();
    const { trackRecordingAborted, trackRecordingShareEnded } = useFaroTracking();

    let consecutiveEmptyRecorderCount = 0;
    let isRecordingAborted = false;
    let currentDomainType = RecorderDomainType.NONE;
    let recordingStartedAt = 0;
    let resolvedMimeType: string | undefined;
    let isMimeTypeResolved = false;
    let healthCheckSizeHistory: number[] = [];
    // 점검 문맥(응시 시작 전)에서 공유가 끊겼을 때 호출할 콜백. null 이면 기존 리다이렉트 흐름을 탄다.
    let preflightShareEndedHandler: (() => void) | null = null;

    // 세그먼트 하나 = 완결된 독립 webm 파일. 회전 주기가 곧 caution 직전 커버리지(0~SEGMENT_DURATION_MS)다.
    //
    // 이전 구현은 1초마다 새 MediaRecorder 를 만들어 큐에 3개를 살려뒀다(롤오버 순간 4개).
    // 같은 화면을 동시에 3~4번 인코딩하는 셈이라 응시자 CPU 를 그만큼 먹었다. MediaRecorder 는
    // '완결된 blob 전체'에 대해서만 재생을 보장하므로(부분 청크 이어붙이기는 재생 불가) 링버퍼
    // 대신 순차 stop/restart 로 매번 완결된 파일을 만든다 — 동시 인코더는 항상 1개다.
    const SEGMENT_DURATION_MS = 5000;

    let segmentIndex = 0;
    let rotationTimerId: ReturnType<typeof setTimeout> | null = null;
    let pendingStopTimerId: ReturnType<typeof setTimeout> | null = null;
    // 세션 세대. stopRecording/startRecording 마다 증가시켜, 늦게 도착한 타이머·이벤트
    // 콜백이 새 세션의 recorder 를 건드리지 못하게 한다.
    let sessionId = 0;
    // 저장 중인 세그먼트에 묶인 actionId 들. 저장 창(4~11초) 안에 caution 이 더 들어오면
    // 같은 클립을 각 actionId 로 올린다 — 백엔드 인제스트가 숫자 파일명만 받으므로
    // ({env}/{domain}/{id}/{uuid}/{actionId}.webm) 접미사를 붙일 수 없고, 붙일 이유도 없다.
    let savingActionIds: number[] = [];

    const resolveDomainId = (domainType: RecorderDomainType) =>
        domainType === RecorderDomainType.CONTEST ? assessmentStore.contestId : assessmentStore.proSkillAssure.assessmentId;

    // 업로드는 이벤트 발생 후 4~11초 뒤에 일어나고, 그 사이 응시 종료/이탈로 store 가
    // reset 되면 assessmentId 가 0 이 된다. 실제로 opt 버킷에 custom_assessment/0/... 로
    // 떨어진 클립이 쌓여 있고, 분석기는 실제 assessmentId prefix 만 보므로 영영 못 찾는다.
    // 녹화 시작 시점의 id 를 고정해 두고 업로드에 쓴다.
    let recordingDomainId = 0;

    // 지원되는 코덱 중 RECORDER_MIME_TYPE_PREFERENCES 순서대로 첫 매치를 1회만 계산해 캐시한다.
    // (매초 setInterval 마다 isTypeSupported 를 재호출하지 않도록 memo)
    const getRecorderMimeType = (): string | undefined => {
        if (isMimeTypeResolved) return resolvedMimeType;
        isMimeTypeResolved = true;
        if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
            resolvedMimeType = undefined; // 판단 불가 → 브라우저 기본값에 위임
        } else {
            resolvedMimeType = RECORDER_MIME_TYPE_PREFERENCES.find(t => MediaRecorder.isTypeSupported(t));
        }
        return resolvedMimeType;
    };

    const redirectToEntry = (domainType: RecorderDomainType) => {
        loadingStore.openLoading("");
        if (domainType === RecorderDomainType.CONTEST) {
            router.replace(`/dashboard/contest/${assessmentStore.contestId}`);
        } else if (domainType === RecorderDomainType.PRO_SKILL_ASSURE) {
            router.replace(`/learning/proSkillAssure/${assessmentStore.proSkillAssure.assessmentId}`);
        }
    };

    const openRecordingFailAlert = (domainType: RecorderDomainType) => {
        openGenericAlert(
            {
                title: t('skillCertify-basic-proctoring-recording-fail-title'),
                contents: t('skillCertify-basic-proctoring-recording-fail-description', {email: '<a href="mailto:support@codepresso.io">support@codepresso.io</a>'}),
                popupClass: 'dark',
            },
            {
                confirm: { title: t('common-ok'), event: () => redirectToEntry(domainType) },
                close: { event: () => redirectToEntry(domainType) },
            }
        );
    };

    /**
     * 이 인스턴스가 시작한 스트림이 아직 "우리 것"인가.
     *
     * 점검(시작 페이지)에서 획득한 스트림은 응시 화면이 그대로 이어받는다(공유 1회). 그래서
     * 세그먼트 회전 루프는 시작 페이지의 서비스 인스턴스가 계속 돌리는데, 응시 화면이
     * 페이지를 떠나며 `stopRecording()` 을 부르면 `recorderStream` 이 null 이 된다.
     * 그 순간부터 이 루프는 "주인 없는 루프"이므로 조용히 멈춰야 한다.
     */
    const ownsActiveStream = () =>
        !!stream.value && assessmentStore.recorderStream === stream.value;

    /**
     * 화면 공유 트랙이 아직 살아 있는가.
     *
     * '녹화 불가' 팝업은 **트랙은 살아 있는데 인코더가 프레임을 쓰지 못하는** 경우를 위한 것이다.
     * 트랙이 이미 ended 면 그것은 "공유가 중지됨"이고, 사용자의 뒤로가기·공유 중지 버튼이
     * 원인일 수 있다 — 그 경로는 `onended`(리다이렉트) 나 라우팅 정리가 담당한다.
     * 여기서 팝업을 띄우면 "진짜 녹화가 안 되는 경우"가 아닌데도 실패를 알리게 된다.
     */
    const isCaptureTrackLive = () =>
        assessmentStore.recorderStream?.getVideoTracks().some(track => track.readyState === 'live') === true;

    const handleHealthCheck = (size: number, recorderLifetimeMs: number) => {
        if (isRecordingAborted) return;
        // 다른 소유자가 이미 정리했거나(페이지 이탈) 트랙이 끊긴 뒤의 측정치는 고장 신호가 아니다.
        if (!ownsActiveStream() || !isCaptureTrackLive()) return;

        // abort 시 원인 판별용 이력 — 카운트 제외되는 측정치도 기록해 전체 흐름을 남긴다.
        healthCheckSizeHistory.push(size);
        if (healthCheckSizeHistory.length > HEALTH_CHECK_HISTORY_LIMIT) healthCheckSizeHistory.shift();

        // 신뢰할 수 없는 구간의 측정치는 카운트하지 않는다 (false positive 방지).
        const inStartupGrace = Date.now() - recordingStartedAt < HEALTH_CHECK_GRACE_MS;
        const isUnfocused = typeof document !== 'undefined' && (document.hidden || !document.hasFocus());
        const tooShort = recorderLifetimeMs < MIN_RECORDER_LIFETIME_MS;
        if (inStartupGrace || isUnfocused || tooShort) return;

        if (size >= MIN_VIDEO_BLOB_SIZE) {
            consecutiveEmptyRecorderCount = 0;
            return;
        }
        consecutiveEmptyRecorderCount += 1;
        if (consecutiveEmptyRecorderCount >= CONSECUTIVE_FAILURE_THRESHOLD) {
            triggerRecordingAbort();
        }
    };

    const triggerRecordingAbort = () => {
        // 사용자의 뒤로가기·공유 중지로 스트림이 사라진 뒤에 늦게 도착한 신호로는 팝업을 띄우지 않는다.
        if (!ownsActiveStream()) {
            isRecordingAborted = true;
            stopRecording();
            return;
        }
        isRecordingAborted = true;
        // stopRecording() 이 stream 을 정리하기 전에 track 상태를 캡처해야 한다.
        const videoTrack = assessmentStore.recorderStream?.getVideoTracks()[0];
        trackRecordingAborted({
            domainType: currentDomainType,
            domainId: resolveDomainId(currentDomainType),
            blobSizeHistory: healthCheckSizeHistory.join(','),
            consecutiveEmptyCount: consecutiveEmptyRecorderCount,
            trackMuted: videoTrack?.muted,
            trackReadyState: videoTrack?.readyState,
        });
        stopRecording();
        openRecordingFailAlert(currentDomainType);
    };

    const initRecordingSession = (domainType: RecorderDomainType) => {
        // 재진입 가드: 이전에 살아있던 세션을 확실히 닫고 시작한다. 없으면 이전 recorder 와
        // 회전 타이머가 큐를 공유한 채 서로의 recorder 를 stop 시키며 동시 인코더가 늘어난다.
        if (assessmentStore.recorderStream || assessmentStore.recorderQueue.length > 0) {
            stopRecording();
        }
        sessionId++;
        consecutiveEmptyRecorderCount = 0;
        isRecordingAborted = false;
        currentDomainType = domainType;
        recordingDomainId = resolveDomainId(domainType);
        recordingStartedAt = Date.now();
        healthCheckSizeHistory = [];
    };

    const acquireDisplayStream = async (domainType: RecorderDomainType) => {
        stream.value = await navigator.mediaDevices.getDisplayMedia({
            video: {
                cursor: "always",
                displaySurface: "monitor",
                width: { ideal: 1280 },
                height: { ideal: 720 },
                frameRate: { ideal: 5 }
            } as MediaTrackConstraints,
            audio: false
        });

        assessmentStore.recorderStream = stream.value;

        assessmentStore.recorderStream.getVideoTracks()[0].onended = async () => {
            const isExtendedDisplay = (window.screen as Screen & { isExtended?: boolean }).isExtended === true;
            const isCautionPopupAlreadyOpen = dialogStore.multiOption.some(
                o => o.type === DialogType.CautionMultiMonitorPopup
            );
            // 다중 모니터 감독이 켜진 경우에만, 확장 디스플레이로 인한 onended는 멀티모니터 주의 플로우가 처리하므로
            // 리다이렉트를 건너뛴다. 감독이 꺼진 시험에서는 확장 디스플레이여도 "사용자의 공유 중지"이므로 시작 페이지로 이동한다. (GP flow)
            const isHandledByMultiMonitorFlow =
                (isExtendedDisplay && assessmentStore.proctoringConfig.useMultiMonitorProctor) || isCautionPopupAlreadyOpen;

            const endedTrack = assessmentStore.recorderStream?.getVideoTracks()[0];
            trackRecordingShareEnded({
                domainType,
                domainId: resolveDomainId(domainType),
                isExtendedDisplay,
                handledByMultiMonitorFlow: isHandledByMultiMonitorFlow,
                trackMuted: endedTrack?.muted,
                trackReadyState: endedTrack?.readyState,
            });

            await stopRecording();

            // ASMT-PROCTOR-14 BR7: 점검은 통과했지만 아직 [평가 시작]을 누르지 않은 구간에서 공유를 끊은 경우다.
            // 여기서 redirectToEntry 를 타면 이미 시작 페이지에 있으므로 router.replace 가 no-op 이 되고
            // openLoading 만 남아 화면이 영구 로딩으로 굳는다. 점검 상태를 되돌리는 것은 호출자의 몫이다.
            if (preflightShareEndedHandler) {
                preflightShareEndedHandler();
                return;
            }

            if (isHandledByMultiMonitorFlow) {
                return;
            }
            redirectToEntry(domainType);
        }
    };

    /**
     * 화면 공유 선택기를 사용자가 그냥 닫았는가(권한 거부가 아니라).
     *
     * 브라우저는 "사용자가 선택기를 닫음"과 "권한이 거부됨"을 모두 NotAllowedError 로 보고한다.
     * Chrome 은 OS 레벨 거부일 때만 메시지에 'system' 을 넣으므로 그것으로 가른다.
     * 선택기가 뜨기도 전에 온 거부도 취소가 아니다 — Chrome 정책(`ScreenCaptureAllowed=false`)은 요청
     * 즉시 'system' 없는 NotAllowedError 를 돌려줘서, 취소로 보면 토스트만 반복되고 해결 안내가 안 뜬다(REQ-313 QA).
     * 애매하면 실패로 본다 — 해결 안내를 한 번 더 보여주는 쪽이 안전하다.
     */
    const isUserCancelledShare = (error: DOMException | undefined, elapsedMs: number) =>
        error?.name === 'NotAllowedError'
        && !/system/i.test(error?.message ?? '')
        && elapsedMs >= MIN_PICKER_CANCEL_MS;

    const isFullScreenShare = () =>
        // @ts-ignore — displaySurface 는 아직 표준 타입 정의에 없다.
        assessmentStore.recorderStream?.getVideoTracks()[0]?.getSettings().displaySurface === 'monitor';

    const startRecording = async (domainType = RecorderDomainType.NONE): Promise<boolean> => {
        const requestedAt = Date.now();
        try {
            initRecordingSession(domainType);
            await acquireDisplayStream(domainType);

            if (!isFullScreenShare()) {
                stopRecording();
                return new Promise((resolve) => {
                    dialogStore.open({
                        type: DialogType.RecordingFullScreenPopup,
                        props: { isProSkillAssure: learningStore.isProSkillAssure },
                        close: {
                            event: () => {
                                startRecording(domainType).then(resolve)
                                return false;
                            }
                        }
                    })
                });
            }

            startSegment(sessionId);
            return true;
        }catch (e){
            console.error("Error starting screen recording:", e);

            // BR-1750: 응시자가 공유 선택기를 닫은 것은 고장이 아니다. '녹화가 되지 않습니다' 팝업은
            // 원인을 잘못 지목하고, 어차피 그 팝업의 [확인]도 시작 페이지로 보낸다 —
            // 거짓 오류를 한 번 보여줄 이유가 없으므로 바로 시작 페이지로 되돌린다.
            if (isUserCancelledShare(e as DOMException, Date.now() - requestedAt)) {
                redirectToEntry(currentDomainType);
                return false;
            }

            openRecordingFailAlert(currentDomainType);
            return false;
        }
    };

    /**
     * ASMT-PROCTOR-14 (TSK-11689) — 평가 시작 **전** 화면 공유 점검.
     *
     * `startRecording` 과 획득 경로는 같지만 실패를 팝업으로 처리하지 않고 결과 코드로 돌려준다.
     * 점검 문맥에서는 취소/전체화면 아님/권한 실패가 각각 다른 안내(토스트 / 재요청 / OS별 해결)로
     * 갈라지기 때문이다.
     *
     * 성공하면 녹화 세그먼트까지 시작한다 — 이 스트림이 그대로 응시 화면의 녹화 스트림이 되어
     * 화면 공유는 **1회**로 끝난다(진행 페이지는 `recorderStream === null` 일 때만 새로 요청한다).
     */
    const startPreflightRecording = async (
        domainType: RecorderDomainType,
        onShareEnded?: () => void
    ): Promise<ScreenShareCheckOutcome> => {
        const requestedAt = Date.now();
        try {
            preflightShareEndedHandler = onShareEnded ?? null;
            initRecordingSession(domainType);
            await acquireDisplayStream(domainType);

            if (!isFullScreenShare()) {
                stopRecording();
                return { result: ScreenShareCheckResult.NOT_FULL_SCREEN };
            }

            startSegment(sessionId);
            return { result: ScreenShareCheckResult.SUCCESS };
        } catch (e) {
            stopRecording();
            const error = e as DOMException;
            console.error("Screen share preflight failed:", error);

            return isUserCancelledShare(error, Date.now() - requestedAt)
                ? { result: ScreenShareCheckResult.CANCELLED }
                : { result: ScreenShareCheckResult.FAILED, reason: error?.name || 'UnknownError' };
        }
    };

    /** 응시가 실제로 시작되어 점검 문맥을 벗어날 때 호출한다(이후 공유 중단은 기존 리다이렉트 흐름). */
    const releasePreflightGuard = () => {
        preflightShareEndedHandler = null;
    };


    const clearRecorderTimers = () => {
        if (rotationTimerId !== null) clearTimeout(rotationTimerId);
        if (pendingStopTimerId !== null) clearTimeout(pendingStopTimerId);
        rotationTimerId = null;
        pendingStopTimerId = null;
    };

    const currentRecorder = (): RecorderDataModel | undefined => assessmentStore.recorderQueue[0];

    const stopIfActive = (recorderData: RecorderDataModel) => {
        if (recorderData.recorder.state !== 'inactive') recorderData.recorder.stop();
    };

    const startSegment = (session: number) => {
        if (session !== sessionId || !stream.value || isRecordingAborted) return;
        // 다른 소유자가 스트림을 내려놓았다(응시 화면 이탈 등). 죽은 스트림으로 MediaRecorder 를
        // 다시 만들면 InvalidStateError 가 나고, 그게 '녹화 불가' 팝업으로 잘못 승격된다.
        if (!ownsActiveStream()) {
            clearRecorderTimers();
            return;
        }
        try {
            const recorderData = createMediaRecorder(stream.value, segmentIndex++);
            // 회전으로 버려지는 세그먼트의 실 데이터량으로 health check 를 유지한다.
            recorderData.recorder.ondataavailable = (event: BlobEvent) => {
                if (session !== sessionId) return;
                handleHealthCheck(event.data.size, Date.now() - recorderData.createdAt);
            };
            assessmentStore.recorderQueue = [recorderData];
            rotationTimerId = setTimeout(() => rotateSegment(session), SEGMENT_DURATION_MS);
        } catch (e) {
            // 여기서 실패하면 이전 세그먼트는 이미 stop 됐고 새 세그먼트도 없다 —
            // 녹화 없이 조용히 응시가 계속되지 않도록 중단 플로우로 보낸다.
            console.error('Failed to start recording segment:', e);
            assessmentStore.recorderQueue = [];
            // 단, 트랙이 이미 끊긴 뒤라면 원인은 "녹화 고장"이 아니라 "공유 중지"다.
            if (!isCaptureTrackLive()) {
                stopRecording();
                return;
            }
            triggerRecordingAbort();
        }
    };

    const rotateSegment = (session: number) => {
        if (session !== sessionId) return;
        rotationTimerId = null;
        const recorderData = currentRecorder();
        // 저장 중인 세그먼트는 saveVideo 가 자기 타이머로 stop 하고 다음 세그먼트를 연다.
        if (recorderData && !recorderData.isSaving) stopIfActive(recorderData);
        startSegment(session);
    };

    const createMediaRecorder = (stream: MediaStream, index: number): RecorderDataModel => {
        const chunks: Blob[] = [];
        const mimeType = getRecorderMimeType();
        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

        recorder.start();
        // @ts-ignore
        return { index, recorder, chunks, isSaving: false, createdAt: Date.now() };
    };

    const stopRecording = () => {
        // 세션 무효화: 아직 도착하지 않은 stop/dataavailable 콜백이 다음 세션을 건드리지 못한다.
        sessionId++;
        savingActionIds = [];
        clearRecorderTimers();
        if (assessmentStore.recorderIntervalIds.length > 0) {
            assessmentStore.recorderIntervalIds.forEach(id => clearInterval(id));
            assessmentStore.recorderIntervalIds = [];
        }
        while (assessmentStore.recorderQueue.length > 0) {
            const recorderData = assessmentStore.recorderQueue.shift();
            if (recorderData) stopIfActive(recorderData);
        }
        assessmentStore.recorderStream?.getTracks().forEach(track => track.stop());
        assessmentStore.recorderQueue = [];
        assessmentStore.recorderTime = [];
        assessmentStore.recorderStream = null;
    };

    // caution 이벤트 클립: 진행 중인 세그먼트를 이벤트 이후 timeoutDuration 까지 연장한 뒤 닫는다.
    // 결과 blob 은 (세그먼트 시작 ~ 이벤트+timeout) 을 담은 완결된 webm 이라 그대로 재생된다.
    // ASMT-PROCTOR-05: 위반 이벤트별 녹화 클립(actionId.webm) 저장 — 이벤트 후 Tab=11s(isLongRender) / 그 외 4s 연장 후 S3 업로드
    const saveVideo = (domainType: RecorderDomainType, actionId: number, isLongRender = false) => {
        const recorderData = currentRecorder();
        if (!recorderData) {
            return;
        }
        if (recorderData.isSaving) {
            // 저장 창(4~11초) 안에 들어온 caution — 같은 세그먼트가 그 시점을 담고 있으므로
            // 완료 시 같은 blob 을 이 actionId 로도 올린다. 예전엔 그냥 버려져 증거가 없었다.
            if (!savingActionIds.includes(actionId)) savingActionIds.push(actionId);
            return;
        }

        const session = sessionId;
        recorderData.isSaving = true;
        savingActionIds = [actionId];
        if (rotationTimerId !== null) {
            clearTimeout(rotationTimerId);
            rotationTimerId = null;
        }

        recorderData.recorder.ondataavailable = (event: BlobEvent) => {
            if (session !== sessionId) return;
            handleHealthCheck(event.data.size, Date.now() - recorderData.createdAt);
            if (event.data.size > 0) {
                const actionIds = savingActionIds;
                savingActionIds = [];
                actionIds.forEach(id => {
                    const completeBlob = new Blob([event.data], { type: 'video/webm' });
                    uploadVideoFile(`${id}.webm`, domainType, completeBlob)
                        // 업로드 실패는 응시를 막지 않지만 조용히 사라지면 증거가 유실된다.
                        .catch(e => console.warn('recording clip upload failed', e));
                });
            }
        };

        pendingStopTimerId = setTimeout(() => {
            pendingStopTimerId = null;
            if (session !== sessionId) return;
            stopIfActive(recorderData);
            startSegment(session);
        }, isLongRender ? 11000 : 4000);
    };

    const uploadVideoFile = async (fileName: string, type: RecorderDomainType, videoBlob: Blob) => {
        const recordeApi = new RecordeApi();

        const preSignedUrl = await recordeApi.getPreSignedUrl({
            file_name: fileName,
            domain: type,
            domain_id: recordingDomainId || resolveDomainId(type),
            user_uuid: userStore.userV2.uuid,
            env: config.public.ENVIRONMENT as string,
        });

        await recordeApi.uploadVideo(preSignedUrl, videoBlob);
    }

    return {
        startRecording,
        startPreflightRecording,
        releasePreflightGuard,
        stopRecording,
        saveVideo
    };
}
